import { exhaustiveCheck } from "@lobby/core/src";
import { Game, Lobby, type TGames } from "@lobby/core/src/entities";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import type { Output } from "@lobby/api-client/src/codegen";

type TBannerAction = Output<"Slider.getSimplified">["banners"][number]["action"];

type TCategoryList = Output<"Game.getCategoriesList">["data"][number][] | undefined;

type TBannerDevice = "mobile" | "desktop";

type TNavigate = ReturnType<typeof useNavigate>;

type TBannerItem = {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  buttonTitle: string;
  action?: () => void;
};

function createAction(
  action: TBannerAction,
  navigate: TNavigate,
  gameList: TGames | undefined,
  categoryList: TCategoryList,
) {
  if (!action || !action.value) return;

  const type = action.type;

  switch (type) {
    case "category": {
      const categoryId = Number(action.value);
      const category = categoryList?.find((c) => c.id === categoryId);

      if (!category) return;

      if (category.actionType === "wallet") {
        return () => navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            modal: "profile",
            profileSection: "wallet"
          }),
        })
      } else {
        return ()=> navigate({
          to: ".",
          replace: true,
          hash: "navigate-category-anchor",
          search: (prev) => ({
            ...prev,
            game: undefined,
            pid: undefined,
            cid: categoryId,
          }),
        });
      }
    }

    case "game": {
      const game = gameList?.find((g) => g.id === Number(action.value));
      if (!game) return;

      return () =>
        navigate({
          to: "/game/$gameId",
          params: { gameId: game.stringId },
        });
    }

    case "url": {
      return () => {
        const url = action.value;

        if (!url) return;

        window.open(url, "_blank");
      };
    }

    case "tag":
      break;
    case "provider": {
      const pid = Number(action.value);
      if (Number.isNaN(pid)) return;

      return () =>
        navigate({
          to: ".",
          replace: true,
          search: () => ({
            pid,
          }),
        });
    }
    default:
      exhaustiveCheck(type);
  }
}

function useBanners(device: TBannerDevice): TBannerItem[] {
  const navigate = useNavigate();

  const { data } = Lobby.useBanner();
  const { data: gameList } = Game.useList();
  const { data: categoryList } = Game.useCategoriesList();

  return useMemo<TBannerItem[]>(() => {
    const banners = data?.banners ?? [];

    return banners.reduce<TBannerItem[]>((acc, banner, idx) => {
      const image = banner.image[device];
      if (!image) return acc;

      const action = banner.action;

      acc.push({
        id: idx,
        image,
        title: banner.title,
        subtitle: banner.subtitle,
        buttonTitle: banner.button,
        action: createAction(action, navigate, gameList, categoryList),
      });

      return acc;
    }, []);
  }, [data?.banners, device, gameList, categoryList, navigate]);
}

export const useMobileBanners = (): TBannerItem[] => useBanners("mobile");
export const useDesktopBanners = (): TBannerItem[] => useBanners("desktop");
